import mqtt, { MqttClient } from 'mqtt';
import { MQTT_CONFIG, MQTT_INSTANCE_TOPICS } from './constant';
import { logger } from './analyticsLogger';

let client: MqttClient | null = null;
let connectionAttempts = 0;
let isConnecting = false;
let connectionEnabled = true;

const instanceId = process.env.REACT_APP_INSTANCE_ID || 'dtdl';
const INSTANCE_TOPIC = MQTT_INSTANCE_TOPICS[instanceId] || MQTT_INSTANCE_TOPICS.dtdl;

const rawUrl = process.env.REACT_APP_MQTT_BROKER_URL || '';
const MQTT_URL = rawUrl ? `${MQTT_CONFIG.PROTOCOL}://${rawUrl}:${MQTT_CONFIG.PORT}${MQTT_CONFIG.WS_PATH}` : '';

// Implement exponential backoff
function getReconnectDelay(): number {
  const delay = Math.min(
    MQTT_CONFIG.BASE_RECONNECT_DELAY * Math.pow(2, connectionAttempts), 
    MQTT_CONFIG.MAX_RECONNECT_DELAY
  );
  return delay;
}

const MQTT_OPTIONS = {
  username: process.env.REACT_APP_OA_MQTT_USERNAME || '',
  password: process.env.REACT_APP_OA_MQTT_PASSWORD || '',
  reconnectPeriod: 0, // Disable automatic reconnect, we'll handle it manually
  clean: MQTT_CONFIG.CLEAN,
  connectTimeout: MQTT_CONFIG.CONNECT_TIMEOUT,
  protocolVersion: MQTT_CONFIG.PROTOCOL_VERSION,
  protocol: MQTT_CONFIG.PROTOCOL,
};

export function connectToMQTT(): Promise<MqttClient | null> {
  return new Promise((resolve) => {
    // Check if MQTT URL is configured
    if (!MQTT_URL || !connectionEnabled) {
      logger.warn('MQTT', 'Not configured or disabled');
      return resolve(null);
    }

    // Return existing connected client
    if (client && client.connected) {
      return resolve(client);
    }

    // Prevent multiple simultaneous connection attempts
    if (isConnecting) {
      return resolve(null);
    }

    // Check if we've exceeded max attempts
    if (connectionAttempts >= MQTT_CONFIG.MAX_RECONNECT_ATTEMPTS) {
      logger.warn('MQTT', 'Max reconnection attempts reached. Disabling MQTT.', {
        attempts: connectionAttempts,
        max: MQTT_CONFIG.MAX_RECONNECT_ATTEMPTS
      });
      connectionEnabled = false;
      return resolve(null);
    }

    isConnecting = true;
    client = mqtt.connect(MQTT_URL, MQTT_OPTIONS);

    const connectionTimeout = setTimeout(() => {
      logger.warn('MQTT', 'Connection timeout', { timeout: MQTT_CONFIG.CONNECT_TIMEOUT });
      isConnecting = false;
      if (client) {
        client.end();
        client = null;
      }
      resolve(null);
    }, MQTT_CONFIG.CONNECT_TIMEOUT);

    client.on('connect', () => {
      clearTimeout(connectionTimeout);
      logger.mqttConnection('connected');
      connectionAttempts = 0; // Reset on successful connection
      isConnecting = false;
      
      // Resume queue processing when connection is restored
      if (messageQueue.length > 0 && !isProcessingQueue && !queueProcessingScheduled) {
        queueProcessingScheduled = true;
        setTimeout(() => {
          void processMessageQueue();
        }, 0);
      }
      
      resolve(client as MqttClient);
    });

    client.on('error', (err) => {
      clearTimeout(connectionTimeout);
      logger.mqttConnection('error', { message: err.message });
      isConnecting = false;
      connectionAttempts++;
      
      // Schedule reconnection with exponential backoff
      if (connectionAttempts < MQTT_CONFIG.MAX_RECONNECT_ATTEMPTS) {
        const delay = getReconnectDelay();
        logger.mqttReconnect(connectionAttempts, MQTT_CONFIG.MAX_RECONNECT_ATTEMPTS, delay);
        setTimeout(() => {
          // Fire and forget - reconnection will happen in background
          void connectToMQTT();
        }, delay);
      }
      
      resolve(null);
    });

    client.on('close', () => {
      logger.mqttConnection('disconnected');
      isConnecting = false;
    });

    client.on('offline', () => {
      logger.debug('MQTT', 'Client offline');
    });

    client.on('reconnect', () => {
      logger.debug('MQTT', 'Reconnecting');
    });
  });
}

// Non-blocking publish with queue
export interface AnalyticsEvent {
  date: string;
  event_type: string;
  event_category: string;
  event_action: string;
  event_label: string;
  timestamp: {
    event_logtime: number;
    event_logtimestamp: string;
    cdl_time: string;
    cdl_timestamp: string;
    de_ingestion_id: string;
  };
  identifiers: Record<string, string>;
  device: Record<string, string>;
  network: Record<string, string>;
  session: Record<string, string>;
  page: Record<string, string>;
  attribute: {
    description: string;
    channel: string;
    pagename: string;
    [key: string]: string | undefined; // Allow for other optional attributes
  };
  custom_data?: Record<string, any>;
  m_canvas?: {
    source: string;
    type: "Page" | "Component";
    id: string;
    component?: string;
    name: string;
    action: string;
    label: string;
    position?: string;
  };
}

const messageQueue: Array<{ topic: string; message: AnalyticsEvent; retryCount?: number }> = [];
let isProcessingQueue = false;
let queueProcessingScheduled = false;

/**
 * Asynchronously processes messages from the `messageQueue`.
 * It connects to MQTT, batches messages, handles retries with exponential backoff,
 * and drops messages that exceed the maximum retry count or queue size.
 * This function ensures non-blocking processing and resilient message delivery.
 */
async function processMessageQueue() {
  // Prevent concurrent processing
  if (isProcessingQueue || messageQueue.length === 0) {
    queueProcessingScheduled = false;
    return;
  }
  
  isProcessingQueue = true;
  queueProcessingScheduled = false;
  
  // Log queue processing start
  logger.debug('MQTT', 'Starting queue processing', {
    queueSize: messageQueue.length
  });
  
  try {
    // Get connection once at the start
    const mqttClient = await connectToMQTT();
    
    if (!mqttClient || !mqttClient.connected) {
      // Connection unavailable - put all messages back at front
      const messagesToRequeue = messageQueue.splice(0);
      const currentQueueSize = messageQueue.length;
      
      if (currentQueueSize + messagesToRequeue.length < MQTT_CONFIG.MAX_QUEUE_SIZE) {
        messageQueue.unshift(...messagesToRequeue);
        logger.debug('MQTT', 'Connection unavailable, messages re-queued', {
          queueSize: messageQueue.length
        });
      } else {
        // Queue full - drop excess messages
        const availableSpace = MQTT_CONFIG.MAX_QUEUE_SIZE - currentQueueSize;
        messageQueue.unshift(...messagesToRequeue.slice(0, availableSpace));
        logger.warn('MQTT', 'Queue full, dropping messages', { 
          dropped: messagesToRequeue.length - availableSpace,
          queueSize: messageQueue.length
        });
      }
      return;
    }
    
    // Process messages in batches for efficiency
    const MAX_BATCH_SIZE = MQTT_CONFIG.MESSAGE_BATCH_SIZE;
    const MAX_RETRIES = MQTT_CONFIG.MAX_MESSAGE_RETRIES;
    const messagesToProcess: Array<{ topic: string; message: any; retryCount: number }> = [];
    const messagesToDrop: Array<{ topic: string }> = [];
    
    // Collect messages to process (filter out exceeded retries)
    while (messageQueue.length > 0 && messagesToProcess.length < MAX_BATCH_SIZE) {
      const queueItem = messageQueue.shift()!;
      const { topic, message, retryCount = 0 } = queueItem;
      
      if (retryCount >= MAX_RETRIES) {
        messagesToDrop.push({ topic });
        continue;
      }
      
      messagesToProcess.push({ topic, message, retryCount });
    }
    
    // Drop messages that exceeded max retries
    if (messagesToDrop.length > 0) {
      logger.warn('MQTT', 'Messages exceeded max retries, dropping', { 
        count: messagesToDrop.length
      });
      messagesToDrop.forEach(({ topic }) => logger.mqttMessage('dropped', topic));
    }
    
    if (messagesToProcess.length === 0) {
      // Continue processing if there are more messages
      if (messageQueue.length > 0) {
        queueProcessingScheduled = true;
        setTimeout(() => void processMessageQueue(), 0);
      }
      return;
    }
    
    // Send all messages concurrently for maximum efficiency
    const clientRef = mqttClient;
    const publishPromises = messagesToProcess.map(({ topic, message, retryCount }) => {
      const payload = JSON.stringify(message);
      
      return new Promise<{ success: boolean; topic: string; message: any; retryCount: number }>((resolve) => {
        try {
          clientRef.publish(topic, payload, {}, (err) => {
            if (err) {
              logger.error('MQTT', 'Publish failed', { 
                error: err.message,
                retryCount: retryCount + 1
              });
              resolve({ success: false, topic, message, retryCount });
            } else {
              // Log topic and message details only on successful send
              logger.info('MQTT', 'Message sent successfully', {
                topic,
                message, // Log actual message object for console inspection
                messageSize: payload.length
              });
              resolve({ success: true, topic, message, retryCount });
            }
          });
        } catch (publishErr: any) {
          logger.error('MQTT', 'Publish exception', { 
            error: publishErr?.message || String(publishErr)
          });
          resolve({ success: false, topic, message, retryCount });
        }
      });
    });
    
    // Wait for all publishes to complete (using Promise.all with error handling for compatibility)
    const results = await Promise.all(
      publishPromises.map((promise, index) =>
        promise.catch((error) => ({
          success: false as const,
          topic: messagesToProcess[index].topic,
          message: messagesToProcess[index].message,
          retryCount: messagesToProcess[index].retryCount,
          error
        }))
      )
    );
    
    // Process results: re-queue failures, keep track of successes
    const failedMessages: Array<{ topic: string; message: any; retryCount: number }> = [];
    let successCount = 0;
    
    results.forEach((result) => {
      if (result.success) {
        successCount++;
      } else {
        // Re-queue failed messages
        failedMessages.push({ 
          topic: result.topic, 
          message: result.message, 
          retryCount: result.retryCount + 1 
        });
      }
    });
    
    // Re-queue failed messages
    if (failedMessages.length > 0) {
      const currentQueueSize = messageQueue.length;
      const availableSpace = MQTT_CONFIG.MAX_QUEUE_SIZE - currentQueueSize;
      
      if (availableSpace >= failedMessages.length) {
        messageQueue.push(...failedMessages);
        logger.debug('MQTT', 'Messages re-queued after failure', {
          count: failedMessages.length
        });
        failedMessages.forEach(({ topic }) => logger.mqttMessage('queued', topic));
      } else {
        // Queue full - add what we can, drop the rest
        messageQueue.push(...failedMessages.slice(0, availableSpace));
        logger.warn('MQTT', 'Queue full, dropping failed messages', { 
          dropped: failedMessages.length - availableSpace,
          queueSize: messageQueue.length
        });
        failedMessages.slice(availableSpace).forEach(({ topic }) => logger.mqttMessage('dropped', topic));
      }
    }
    
    // Log batch completion
    logger.debug('MQTT', 'Batch processed', {
      sent: successCount,
      failed: failedMessages.length,
      remainingQueueSize: messageQueue.length
    });
    
    // Continue processing if there are more messages
    if (messageQueue.length > 0) {
      queueProcessingScheduled = true;
      setTimeout(() => void processMessageQueue(), 0);
    } else {
      logger.debug('MQTT', 'Queue processing completed, queue empty');
    }
  } catch (error: any) {
    logger.error('MQTT', 'Queue processing error', { 
      error: error?.message || String(error),
      queueSize: messageQueue.length
    });
  } finally {
    isProcessingQueue = false;
  }
}

/**
 * Publish a message directly to MQTT (non-blocking, for immediate send)
 * Used when connection is ready and we want to skip queue overhead
 */
/**
 * Publish a message directly to MQTT (non-blocking, for immediate send)
 * Used when connection is ready and we want to skip queue overhead.
 * This function attempts to publish the message without queuing it.
 * 
 * @param topic - The MQTT topic to publish to.
 * @param message - The message payload to publish (will be JSON stringified).
 * @returns A promise that resolves to `true` if the message was successfully published, `false` otherwise.
 */
async function publishDirectly(topic: string, message: AnalyticsEvent): Promise<boolean> {
  const mqttClient = await connectToMQTT();
  
  if (!mqttClient || !mqttClient.connected) {
    return false; // Connection not ready, fall back to queue
  }
  
  const payload = JSON.stringify(message);
  
  return new Promise<boolean>((resolve) => {
    try {
      mqttClient.publish(topic, payload, {}, (err) => {
        if (err) {
          logger.error('MQTT', 'Direct publish failed', { 
            error: err.message
          });
          resolve(false);
        } else {
          // Log topic and message details only on successful send
          logger.info('MQTT', 'Message sent successfully', {
            topic,
            message, // Log actual message object for console inspection
            messageSize: payload.length
          });
          resolve(true);
        }
      });
    } catch (publishErr: any) {
      logger.error('MQTT', 'Direct publish exception', { 
        error: publishErr?.message || String(publishErr)
      });
      resolve(false);
    }
  });
}

/**
 * Publish a message to MQTT (non-blocking)
 * 
 * Topic is determined once per instance based on REACT_APP_INSTANCE_ID:
 * - 'dtdl' → 'dtdl.website'
 * - 'payzy' → 'payzy.website'
 * 
 * All messages from the same instance use the same topic, so it's set once
 * and reused for all messages. This is more efficient than passing topic per message.
 * 
 * Optimization: For sparse/slow events, sends directly if connection is ready.
 * For rapid events or when connection unavailable, uses queue for batching/retry.
 * 
 * @param message - Message payload to publish (will be JSON stringified)
 * @param topic - Optional override topic. If not provided, uses INSTANCE_TOPIC (recommended)
 */
export function publishMQTTMessage(message: AnalyticsEvent, topic?: string): void {
  // Use provided topic or fall back to instance topic (set once per instance)
  // Topic should be the same for all messages from the same instance/page
  const actualTopic = (topic && topic.trim() !== '') ? topic.trim() : INSTANCE_TOPIC;
  
  // Non-blocking: check if we can send directly or need to queue
  if (!MQTT_URL) {
    logger.debug('MQTT', 'Not configured, skipping message');
    return;
  }

  // If queue is empty and connection is ready, try direct send (skip queue for sparse events)
  if (messageQueue.length === 0 && client && client.connected && !isProcessingQueue) {
    // Fire and forget - try direct send, fall back to queue if it fails
    void publishDirectly(actualTopic, message).then((success) => {
      if (!success) {
        // Direct send failed, add to queue for retry
        if (messageQueue.length < MQTT_CONFIG.MAX_QUEUE_SIZE) {
          messageQueue.push({ topic: actualTopic, message, retryCount: 0 });
          logger.debug('MQTT', 'Direct send failed, message queued');
          // Schedule queue processing
          if (!queueProcessingScheduled && !isProcessingQueue) {
            queueProcessingScheduled = true;
            setTimeout(() => void processMessageQueue(), 0);
          }
        }
      }
    });
    return; // Exit early - direct send attempted
  }

  // Use queue for: batching rapid events, retry logic, or when connection unavailable
  if (messageQueue.length >= MQTT_CONFIG.MAX_QUEUE_SIZE) {
    logger.warn('MQTT', 'Message queue full, dropping message', { 
      queueSize: messageQueue.length,
      maxSize: MQTT_CONFIG.MAX_QUEUE_SIZE
    });
    logger.mqttMessage('dropped', actualTopic);
    return;
  }

  // Store the actual topic in queue (same topic for all messages from this instance)
  messageQueue.push({ topic: actualTopic, message, retryCount: 0 });
  
  // Log only status when queuing
  logger.mqttMessage('queued', actualTopic);
  logger.debug('MQTT', 'Message queued', {
    queueSize: messageQueue.length
  });
  
  // Schedule queue processing if not already scheduled
  // Using setTimeout(0) defers execution to next event loop tick, making this non-blocking
  // Only schedule if not already scheduled to prevent multiple concurrent processing attempts
  if (!queueProcessingScheduled && !isProcessingQueue) {
    queueProcessingScheduled = true;
    setTimeout(() => {
      // Use void to explicitly ignore the promise (non-blocking)
      void processMessageQueue();
    }, 0);
  }
}

// Clean up on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (client) {
      client.end();
    }
    // Clear message queue on unload to prevent memory leaks in SPAs
    messageQueue.length = 0; 
  });
}

// Reset connection state (useful for testing or manual intervention)
export function resetMQTTConnection() {
  connectionAttempts = 0;
  connectionEnabled = true;
  isConnecting = false;
  queueProcessingScheduled = false;
  if (client) {
    client.end();
    client = null;
  }
}

// Export health metrics for monitoring
export function getMQTTHealthMetrics() {
  return {
    queueSize: messageQueue.length,
    isConnected: client?.connected || false,
    connectionAttempts,
    isConnecting,
    connectionEnabled,
    isProcessingQueue,
    queueProcessingScheduled,
  };
}